import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 3.4 visual preview at /preview/lesson/3.4
 * "The audit step: catching what is wrong before you ship"
 */

export default function LessonPreviewPage3_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 3 · Building with AI · Lesson 3.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The audit step: catching what is wrong before you ship
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Judgment · Character · Vocabulary</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        Two-pass audit on a real draft
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="The minute before you hit submit.">
      <p className="mt-6 text-text/90">
        It is 11:47 PM. The assignment is due at midnight. You have a draft. It is
        good enough. You are tired. Your finger is on the submit button.
      </p>
      <p className="mt-4 text-text/90">
        Two possible futures sit in front of you.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Future A · You hit submit
          </p>
          <p className="mt-3 text-text/90">
            Three days later the teacher pulls you aside. There is a stat in
            paragraph 4 that does not match the source. There is a sentence in
            paragraph 6 that sounds nothing like the rest of the essay. The teacher
            asks you to explain both. You cannot. The conversation ends with
            &ldquo;academic integrity meeting on Friday.&rdquo;
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Future B · You spend 10 more minutes
          </p>
          <p className="mt-3 text-text/90">
            You run a two-pass audit. Pass one catches the bad stat. Pass two catches
            the sentence that does not sound like you. You fix both. You submit at
            11:58 PM. The grade comes back fine. No conversation. No meeting.
          </p>
        </div>
      </div>
      <p className="mt-6 text-text/90">
        The difference between the two futures is ten minutes of work and one habit.
        That habit is the audit step. It is the cheapest insurance policy in school.
      </p>
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Why this lesson exists
        </p>
        <p className="mt-3 text-text/90">
          Every adult job that involves writing has an audit step. Lawyers call it
          review. Engineers call it code review. Accountants call it audit. Editors
          call it edit. The skill is the same: read your own work like a stranger
          who is trying to find the broken thing.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 3.3" title="Three questions on the draft step.">
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define voice fingerprint." />
        <ReviewItem n="02" q="Name the three drafting modes, and when you would use each." />
        <ReviewItem n="03" q="What is the difference between drafting with AI and AI drafting for you?" />
      </ol>
      <p className="mt-6 text-sm text-muted">
        Drafting and auditing are not the same skill. Drafting is making the thing.
        Auditing is finding what is broken in the thing you made. Most students do
        the first and skip the second. The students who do not skip get the grade.
      </p>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The audit, real version" title="What audit means. The two passes. The shippable test.">
      <Subhead n={1} title="What audit actually is" />
      <p className="mt-4 text-text/90">
        Most students think editing is the same as auditing. Editing is making
        sentences nicer. Auditing is asking, sentence by sentence: is this true, and
        is this mine. Two different questions. Two different passes. You cannot do
        both at the same time. Most students try, miss both.
      </p>
      <p className="mt-4 text-text/90">
        The word comes from real work. In an audit, someone independent reads what
        you produced and looks for things that do not check out. In school, nobody
        is going to do that for you before you submit. You have to be that person on
        your own work. That is the move.
      </p>
      <AuditFrame />

      <Subhead n={2} title="The thing you have to name" className="mt-12" />
      <p className="mt-4 text-text/90">
        There is a word for what passes the audit. With the word, you have a target.
        Without the word, you submit anything that is &ldquo;done.&rdquo; Done and
        shippable are not the same.
      </p>
      <VocabCard
        term="Provenance"
        definition="Where every claim in your work actually came from, traced back to a source you opened with your own eyes. Provenance is not a footnote. It is the answer to the question: if a teacher or boss put their finger on this sentence, could you show them, in under ten seconds, the exact place you got it? Work without provenance is not ready. It is rough."
        analogy="A diamond comes with a paper trail: this mine, this cutter, this dealer, this store. The diamond is not worth less without the paper, but it is harder to defend. Writing with AI works the same way. The sentences look the same on the page. The ones with provenance survive the question. The ones without it do not."
        memoryHook="Every claim has a source you opened. Every sentence sounds like you. Or it is not ready to ship."
        why="AI will hand you confident sentences that are not tied to anything real. If you ship those, your name is on a claim you cannot trace. Provenance is the audit move that protects your grade, your reputation, and your standing as a thinker. It is also the habit that separates students who get trusted with real work from students who do not."
      />

      <Subhead n={3} title="The two-pass audit" className="mt-12" />
      <p className="mt-4 text-text/90">
        One pass for facts. One pass for voice. In that order. The fact pass first
        because broken facts kill the grade fastest and a missing source is the move
        teachers spot-check. The voice pass second because once the facts are right,
        the voice is what tells the teacher whether you actually wrote this.
      </p>
      <TwoPass />

      <Subhead n={4} title="The shippable test" className="mt-12" />
      <p className="mt-4 text-text/90">
        After both passes, run one last check. The shippable test is three questions
        you ask out loud. If you cannot answer yes to all three, you are not done.
      </p>
      <ShippableTest />
    </Section>
  );
}

function AuditFrame() {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
          Editing only · Not enough
        </p>
        <p className="mt-3 text-text/90">
          You read the draft. You fix typos. You rephrase a sentence that sounds
          clunky. You change one word to a better word. You submit.
        </p>
        <p className="mt-3 text-text/80">
          What you missed: the stat that does not match the source. The citation
          that does not exist. The paragraph that AI wrote in a voice that is not
          yours. Editing makes the surface smoother. It does not make the work
          shippable.
        </p>
      </div>
      <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          Auditing · The actual move
        </p>
        <p className="mt-3 text-text/90">
          Two passes. First pass: every claim, every name, every number. Click the
          source. Confirm the claim is on the page. Second pass: every paragraph
          read out loud. Stop on any sentence you could not defend without
          re-reading. Re-think it. Then re-write it.
        </p>
        <p className="mt-3 text-text/80">
          What you have: a draft where you actually thought every idea and
          could explain any paragraph to a teacher without notes.
        </p>
      </div>
    </div>
  );
}

const PASSES = [
  {
    n: '01',
    name: 'Fact audit',
    accent: 'emerald' as const,
    looks: 'Every claim. Every name. Every number. Every quote. Every date. Every URL.',
    moves:
      'For each one, ask: where did this come from. Open the source. Find the line. Confirm the claim is on the page. If you cannot find it, cut the claim or replace it. No exceptions for “it sounds right.” Sounds right is how the cold-open student got caught.',
    fails:
      'If the source does not open. If the source opens but the claim is not on the page. If the source is AI itself. If the claim is suspiciously specific (two decimal points, a percentage with no year) and you cannot find where the specificity came from.',
  },
  {
    n: '02',
    name: 'Author audit',
    accent: 'amber' as const,
    looks:
      'Every paragraph, read out loud at normal speed. Stop on any sentence you could not defend if a teacher asked you what it means and why it belongs. Voice is the tell. If a sentence does not sound like you, you probably did not think it.',
    moves:
      'Re-think the idea first, from scratch, in your own head. Then write the sentence that comes out. If you skip the re-thinking and only reword AI, you kept AI\'s thought and just changed the wrapper. You still do not own what the paragraph is saying. Re-type any accepted change by hand so the idea passes through your brain, not just your clipboard.',
    fails:
      'You cannot explain what a paragraph is arguing without re-reading it. Sentences sound smoother than your thinking actually is. You could not defend the point in your own words to a teacher on Monday. If author audit fails this hard, the draft is not yours yet. Go back to drafting.',
  },
];

function TwoPass() {
  return (
    <div className="mt-6 space-y-4">
      {PASSES.map((p) => (
        <PassCard key={p.n} pass={p} />
      ))}
    </div>
  );
}

function PassCard({ pass }: { pass: (typeof PASSES)[number] }) {
  const isEmerald = pass.accent === 'emerald';
  const borderCls = isEmerald ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEmerald ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEmerald ? 'text-emerald-300' : 'text-amber';
  const isAuthor = pass.name === 'Author audit';
  return (
    <div className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="font-mono text-xs text-muted">Pass {pass.n}</span>
        <h4 className={`text-lg font-semibold ${labelCls}`}>{pass.name}</h4>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Block label="What you look at" body={pass.looks} />
        <Block label="What you do" body={pass.moves} html />
        <Block label="What makes the pass fail" body={pass.fails} />
      </div>
      {isAuthor && (
        <div className="mt-4 rounded-md border border-rose-500/40 border-dashed bg-rose-500/[0.05] p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
            A common mistake
          </p>
          <p className="mt-2 text-sm text-text/85">
            Some students hear &ldquo;author audit&rdquo; and think the move is
            to reword AI sentences into slang or their vocabulary so it sounds
            like them. That is not the point. If you reword without
            re-thinking, you kept AI&rsquo;s idea and just repainted it. The
            teacher can still tell you did not think it, and more importantly,
            you did not think it. The move is to redo the thinking, then write
            the sentence that comes out of your own head. Voice is the signal
            that you did the thinking, not the goal.
          </p>
        </div>
      )}
    </div>
  );
}

const SHIPPABLE_Qs = [
  {
    q: 'Can I name the source of every claim in this draft, from memory, right now?',
    why: 'If you cannot, you did not actually audit the facts. You skimmed. Go back and re-check the ones you could not name.',
  },
  {
    q: 'If a teacher picked any paragraph at random and asked me to explain it, could I, without re-reading?',
    why: 'If you cannot, you did not write that paragraph. AI did. Rewrite it before you submit.',
  },
  {
    q: 'Does this sound like me out loud?',
    why: 'If reading it aloud feels weird (you would not say these words this way), you probably did not think the idea. Do not just reword the sentence. Go back to the idea, re-think it, then write what actually comes out of your head.',
  },
];

function ShippableTest() {
  return (
    <div className="mt-6 rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        The three shippable questions
      </p>
      <ol className="mt-4 space-y-4">
        {SHIPPABLE_Qs.map((s, i) => (
          <li key={i} className="rounded-md border border-line bg-elevated p-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-amber">
              Q{i + 1}
            </p>
            <p className="mt-2 text-text/90">{s.q}</p>
            <p className="mt-3 border-t border-line pt-3 text-sm text-text/80">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted mr-2">
                If no
              </span>
              {s.why}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* --- Section 4: Your turn · Audit drills --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Run the audit" title="Three suspect sentences. Spot what fails the audit.">
      <p className="mt-6 text-text/90">
        Three sentences from real-looking student drafts. For each, decide what the
        audit catches: a fact problem, a voice problem, both, or neither. Then check
        yourself against the verdict.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <AuditDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

const AUDIT_OPTIONS = [
  { key: 'fact', label: 'Fact audit fails', short: 'Fact' },
  { key: 'voice', label: 'Author audit fails', short: 'Author' },
  { key: 'both', label: 'Both fail', short: 'Both' },
  { key: 'none', label: 'Shippable', short: 'Ship' },
];

type DrillSpec = {
  n: string;
  context: string;
  excerpt: string;
  correct: string;
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    context:
      'Personal statement for college. The student has written about their grandmother teaching them to cook. The rest of the paragraph is in their voice.',
    excerpt:
      'Through this transformative culinary experience, I gained a profound appreciation for the intergenerational transmission of cultural knowledge.',
    correct: 'voice',
    verdict:
      'Author audit fails. The sentence is technically correct and could be defended as true. But the student did not think this. AI thought it and the student accepted the thought. The academic-paper register is the tell. The bigger problem is not that a reader might notice: it is that the student never actually processed what cooking with their grandmother meant to them. Rewording the sentence in slang keeps the same problem: same borrowed thought, new wrapper. The move is to re-think the idea (what did I actually learn from her, what specific moment sticks with me), then write the sentence that comes out. Something like: I started to get why she kept teaching me the same recipe over and over.',
  },
  {
    n: '02',
    context:
      'History essay on the Civil Rights Movement. The student is making a point about economic boycotts. They cite a source.',
    excerpt:
      'The Montgomery Bus Boycott lasted 381 days and reduced bus revenue by 65 percent (Branch, 1988).',
    correct: 'fact',
    verdict:
      'Fact audit fails. The 381-day figure is widely cited and you can find it in Taylor Branch. The 65 percent number is suspiciously specific and not in Branch. Looks like AI invented a stat to support the claim. The audit move: open Branch (the source). Find the line. The 381 days is there. The 65 percent is not. Cut the percentage or find a real source with the actual number.',
  },
  {
    n: '03',
    context:
      'AP Lit essay on The Great Gatsby. The student loves the book and has strong opinions. This is one sentence from a paragraph about the green light.',
    excerpt:
      'The green light is dumb. Gatsby stares at it for five years like it is going to do something. It never does.',
    correct: 'none',
    verdict:
      'Shippable. Voice is clearly the student (specific, blunt, opinionated, in a register a 17-year-old would actually use). The claim is defensible: Gatsby does watch the light, it never delivers what he wants. A teacher might disagree with the take, but disagreeing with a take is not the same as the audit failing. Shippable does not mean safe. It means defendable. This is defendable.',
  },
];

function AuditDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<string | null>(null);
  const correctMeta = AUDIT_OPTIONS.find((o) => o.key === drill.correct);
  const pickedMeta = AUDIT_OPTIONS.find((o) => o.key === picked);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The context
      </p>
      <p className="mt-3 text-sm text-text/85">{drill.context}</p>
      <div className="mt-4 rounded-md border border-line bg-elevated p-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
          The sentence
        </p>
        <p className="mt-2 text-text/90 italic">{drill.excerpt}</p>
      </div>
      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          What does the audit catch?
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {AUDIT_OPTIONS.map((opt) => {
            const isPicked = picked === opt.key;
            const isCorrect = opt.key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-3 text-left text-sm text-text/85 hover:border-amber';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-3 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-3 text-left text-sm text-text';
            return (
              <button
                key={opt.key}
                className={cls}
                onClick={() => setPicked(opt.key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option · {opt.short}
                </span>
                <span className="mt-1 block">{opt.label}</span>
              </button>
            );
          })}
        </div>
        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · {pickedMeta?.label}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That is not what the audit catches here. See the correct answer
              below.
            </p>
          </div>
        )}
        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'What the audit catches' : 'Correct'} · {correctMeta?.label}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define shippable in one sentence." answer="Work you would defend line by line if a teacher pulled you aside on Monday: every claim has a source you could open, and every sentence sounds like you wrote it." />
        <QuickCheckItem n="Q2" q="Name the two audit passes, in order, and what each one is looking for." answer="Pass 1 is the fact audit (every claim, name, number, quote, date, URL has a source you can open). Pass 2 is the author audit (every paragraph read out loud, stop on anything you could not defend or explain without re-reading it)." />
        <QuickCheckItem n="Q3" q="Why fact audit before author audit?" answer="Broken facts kill the grade fastest and missing sources are the easiest thing a teacher spot-checks. Ownership problems can be fixed once you know the facts are right. Doing the author audit first is rearranging the deck chairs on a sinking grade." />
        <QuickCheckItem n="Q4" q="A claim has the right shape (specific number, real-looking citation) but the source does not open. What do you do?" answer="Treat it as the academic dishonesty trap from 3.2. Cut the claim or replace it with one whose source actually opens. Specificity without an openable source is the move that catches students who never check." />
        <QuickCheckItem n="Q5" q="What is the shippable test, in three questions?" answer="One: can I name the source of every claim from memory right now. Two: if a teacher picks any paragraph at random, can I explain it without re-reading. Three: does this sound like me when I read it out loud. Three yeses means shippable. Any no means not done." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Run it before every submit.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 3.4 · Lock card · The audit step
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Two passes before submit. Facts first. Authorship second. If you cannot
          defend a sentence on Monday, it is not shippable. Keep working.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <PassTile n="01" name="Fact audit" accent="emerald" body="Every claim, name, number, quote, date, URL. Open the source. Confirm the line. Cut anything you cannot verify." />
          <PassTile n="02" name="Author audit" accent="amber" body="Every paragraph out loud. Stop on anything you cannot defend. Re-think the idea from scratch. Then write the sentence that comes out. Re-type by hand." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The shippable test · Three yeses or you are not done
          </p>
          <ol className="mt-3 space-y-2 text-text/90">
            <li>
              <span className="font-mono text-[10px] text-amber mr-2">Q1</span>
              Can I name the source of every claim from memory right now?
            </li>
            <li>
              <span className="font-mono text-[10px] text-amber mr-2">Q2</span>
              Can I explain any random paragraph without re-reading it?
            </li>
            <li>
              <span className="font-mono text-[10px] text-amber mr-2">Q3</span>
              Does this sound like me out loud?
            </li>
          </ol>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          The audit step is the cheapest insurance policy in school. Ten minutes
          before submit, every time.
        </p>
      </div>
    </Section>
  );
}

function PassTile({
  n,
  name,
  accent,
  body,
}: {
  n: string;
  name: string;
  accent: 'emerald' | 'amber';
  body: string;
}) {
  const isEm = accent === 'emerald';
  const tagCls = isEm
    ? 'bg-emerald-500/15 text-emerald-300'
    : 'bg-amber/15 text-amber';
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span
          className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tagCls}`}
        >
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 3.5 · Week 3 capstone
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Run the whole chain on one real assignment
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Research, draft, audit, ship. End to end on one piece of work. Every
            move from 3.1 to 3.4 used in sequence. No new vocab. Just the chain,
            applied.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function Block({ label, body, html = false }: { label: string; body: string; html?: boolean }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      {html ? (
        <p
          className="mt-1.5 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      ) : (
        <p className="mt-1.5 text-sm text-text/85">{body}</p>
      )}
    </div>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 3.4 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90">{q}</p>
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p className="mt-3 border-t border-line pt-3 text-sm text-text/85">
          {answer}
        </p>
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    '3.3 review',
    'Audit, real version',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
